import dayjs from "@stadium/helpers/dayjs/plugins";

import {
  DEFAULT_CALENDAR_DATE_OUTPUT_FORMAT,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_TIME_FORMAT,
  EST_ZONE,
  INVALID_YEAR_DATE_FORMAT,
} from "@stadium/helpers/dayjs/constants";

/**
 * Date, Date Time Getters / Formatters
 */
export const currentDate = () => dayjs().format(DEFAULT_DATE_FORMAT);

export const currentDateTime = () => dayjs().format(DEFAULT_DATE_TIME_FORMAT);

export const getDateTime = (date) => dayjs(date);

export const getFormattedDate = (date, format = DEFAULT_DATE_FORMAT) => dayjs(date).format(format);

export const getFormattedDateTime = (date, format = DEFAULT_DATE_TIME_FORMAT) =>
  dayjs(date).format(format);

export const getRelativeTimeFromNow = (dateTime, withoutSuffix = false) =>
  dayjs(dateTime).fromNow(withoutSuffix);

export const getCalendarDate = (dateTime, outputFormats = DEFAULT_CALENDAR_DATE_OUTPUT_FORMAT) =>
  dayjs(dateTime).calendar(dayjs(), outputFormats);

/**
 * Date Checkers
 */
export const isValidDate = (dateStr) => dayjs(dateStr).isValid();
export const isValid = (dateObj) => dateObj.isValid();

/**
 * Date Manipulators
 */
export const getUnitFromDate = (date, unit) => dayjs(date).get(unit);

export const setUnitToDate = (date, unit, value) => dayjs(date).set(unit, value);

export const addDate = (date, unit, value) => dayjs(date).add(value, unit);

export const subtractDate = (date, unit, value) => dayjs(date).subtract(value, unit);

export const startOf = (date, unit) => dayjs(date).startOf(unit);

export const endOf = (date, unit) => dayjs(date).endOf(unit);

export const dateDiff = (date1, date2, diffUnit = "day") =>
  dayjs(date1).diff(dayjs(date2), diffUnit);

/**
 * Zones
 */
export const parseZone = (dateStr, zone = EST_ZONE) => dayjs.tz(dateStr, zone);

export const convertZone = (dateStr, zone = EST_ZONE) => dayjs(dateStr).tz(zone);

export const getZone = () => dayjs.tz.guess();
export const isBetween = (dateObj, datePrimary, dateSecondary, unit = null, indicators = "[]") => {
  return dayjs(dateObj).isBetween(datePrimary, dateSecondary, unit, indicators);
};

export const todaysDate = dayjs();

const getDayjsObj = (date, { inputFormat, tz, parseNull }) => {
  const defaultDate = date;

  if (tz) {
    const tzDate = parseNull && date === null ? dayjs(String(date)) : defaultDate;
    return dayjs.tz(tzDate, inputFormat, tz);
  }
  return dayjs(parseNull && date === null ? String(date) : defaultDate, inputFormat);
};

export const dateFormatter = (date, { tz, format } = {}) => {
  //dayjs(undefined) is same as dayjs()
  const d = date || undefined;

  if (format && tz) return dayjs.tz(d, tz).format(format);
  if (tz) return dayjs.tz(d, tz);
  if (format) return dayjs(d).format(format);

  return dayjs(d);
};

export const dateFormatterWithTimeZone = (
  date,
  { inputFormat, tz, format, parseNull = false } = {},
) => {
  //dayjs(undefined) is same as dayjs()
  const dayjsObj = getDayjsObj(date, { inputFormat, tz, parseNull });

  if (format) {
    return dayjsObj.format(format);
  }

  return dayjsObj;
};

export const dateFormatterToTimezone = (
  date,
  { inputFormat, tz, format, parseNull = false } = {},
) => {
  const dayjsObj = getDayjsObj(date, { inputFormat, parseNull }).tz(tz);

  if (format) {
    return dayjsObj.format(format);
  }

  return dayjsObj;
};

export const convertZoneAndFormat = (dateStr, options = {}) => {
  const defaultOptions = { zone: EST_ZONE, format: DEFAULT_DATE_FORMAT };

  const { zone, format } = { ...defaultOptions, ...options };

  const convertedDate = convertZone(dateStr, zone);
  return convertedDate.format(format);
};

export const add = (date, amount, unit) => dayjs(date).add(amount, unit);

export const currentDateTimeInZone = (zone) => {
  return dayjs.tz(undefined, zone);
};

export const isFuture = (date) => isBefore(currentDate(), date);

export const isBefore = (datePrimary, dateSecondary, unit = null) => {
  if (unit) {
    return dayjs(datePrimary).isBefore(dateSecondary, unit);
  }
  return dayjs(datePrimary).isBefore(dateSecondary);
};

export const isSameOrBefore = (datePrimary, dateSecondary, unit = null) => {
  if (unit) {
    return dayjs(datePrimary).isSameOrBefore(dateSecondary, unit);
  }
  return dayjs(datePrimary).isSameOrBefore(dateSecondary);
};

export const isSame = (datePrimary, dateSecondary, unit = null) => {
  if (unit) {
    return dayjs(datePrimary).isSame(dateSecondary, unit);
  }
  return dayjs(datePrimary).isSame(dateSecondary);
};

export const isSameOrAfter = (datePrimary, dateSecondary, unit = null) => {
  if (unit) {
    return dayjs(datePrimary).isSameOrAfter(dateSecondary, unit);
  }
  return dayjs(datePrimary).isSameOrAfter(dateSecondary);
};

export const isAfter = (datePrimary, dateSecondary, unit = null) => {
  if (unit) {
    return dayjs(datePrimary).isAfter(dateSecondary, unit);
  }
  return dayjs(datePrimary).isAfter(dateSecondary);
};

export const getWeekDaysList = () => dayjs.weekdays();

export const getMonthsList = () => dayjs.months();

export const getWeekDayNameOfGivenDate = (date) => {
  const weekdayList = getWeekDaysList();
  const dayIndex = getUnitFromDate(date, "day");
  return weekdayList[dayIndex];
};

export const getMonthNameOfGivenDate = (date) => {
  const monthList = getMonthsList();
  const monthIndex = getUnitFromDate(date, "month");
  return monthList[monthIndex];
};

export const getDateComponents = (date) => {
  const dayjsObj = getDayjsObj(date, { inputFormat: "YYYY-MM-DD", tz: EST_ZONE });
  return {
    month: dayjsObj.format("MMMM"),
    date: dayjsObj.format("D"),
  };
};

export const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getBirthDateFormat = (date) =>
  date?.includes("0000") ? INVALID_YEAR_DATE_FORMAT : DEFAULT_DATE_FORMAT;

export const durationFormat = (value, inputFormat, outputFormat) =>
  dayjs.duration(value, inputFormat).format(outputFormat);

export default dayjs;

export {
  FULL_MONTH_DATE_TIME_FORMAT,
  MONTH_DATE_DAY_FORMAT,
  FULL_MONTH_DATE_FULL_YEAR_FORMAT,
  DEFAULT_DATE_FORMAT,
  MONTH_DATE_YEAR_DAY_FORMAT,
  EST_ZONE,
  DEFAULT_DATE_TIME_FORMAT,
  MONTH_DATE_YEAR_TIME_EST_FORMAT,
  MONTH_DATE_YEAR_DAY_EST_FORMAT,
  HOUR_MINUTE_TIME_FORMAT,
  INVALID_YEAR_DATE_FORMAT,
  MONTH_DATE_YEAR_TIME_EST_FORMAT_2,
} from "./constants";
